import { useState, useEffect, useMemo } from 'react';
import { formatLocation, validateSupportForm } from '../utils/support-form.utils';

type SupportFormData = {
  email: string;
  name: string;
  primaryPerson: string;
  urgency: string;
  description: string;
  preferredService: string[];
  location: string;
};

type UseSupportRequestFormProps = {
  isOpen: boolean;
  advisorEmail: string;
  advisorName: string;
  searchQuery?: string;
  locationAddress?: string;
  locationCounty?: string;
  locationState?: string;
  locationZipCode?: string;
  onSubmit: (formData: SupportFormData) => Promise<void>;
};

export const useSupportRequestForm = ({
  isOpen,
  advisorEmail,
  advisorName,
  searchQuery = '',
  locationAddress,
  locationCounty,
  locationState,
  locationZipCode,
  onSubmit,
}: UseSupportRequestFormProps) => {
  const [formData, setFormData] = useState<SupportFormData>({
    email: '',
    name: '',
    primaryPerson: '',
    urgency: '',
    description: '',
    preferredService: [],
    location: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const formattedLocation = useMemo(
    () => formatLocation(locationAddress, locationCounty, locationState, locationZipCode),
    [locationAddress, locationCounty, locationState, locationZipCode],
  );

  useEffect(() => {
    if (isOpen) {
      setFormData({
        email: advisorEmail || '',
        name: advisorName || '',
        primaryPerson: '',
        urgency: '',
        description: searchQuery || '',
        preferredService: [],
        location: formattedLocation,
      });
    } else {
      setFormData({
        email: '',
        name: '',
        primaryPerson: '',
        urgency: '',
        description: '',
        preferredService: [],
        location: '',
      });
    }
  }, [isOpen, advisorEmail, advisorName, searchQuery, formattedLocation]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRadioChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name: string, value: string, checked: boolean) => {
    setFormData((prev) => {
      const currentArray = (prev[name as keyof typeof prev] as string[]) || [];
      if (checked) {
        return { ...prev, [name]: [...currentArray, value] };
      } else {
        return { ...prev, [name]: currentArray.filter((item) => item !== value) };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateSupportForm(formData)) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      alert('Failed to submit support request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    isSubmitting,
    handleInputChange,
    handleRadioChange,
    handleCheckboxChange,
    handleSubmit,
  };
};

